// pages/AdhesionPage.tsx avec tracking GTM complet et cohérent
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdhesionFlow from '../components/adhesion/AdhesionFLow';
import { MemberData } from '../types/adhesion.types';
import { useGTMTracking } from '../hooks/useGTMTracking';

const AdhesionPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [referralCode, setReferralCode] = useState<string | null>(null);
    const [pageLoadTime] = useState(Date.now());

    // Hook GTM
    const {
        trackAdhesionStart,
        trackAdhesionFunnelStart,
        trackAdhesionSuccess,
        trackAdhesionConversion,
        trackPageLoadTime,
        trackTechnicalError
    } = useGTMTracking();

    // Mesurer le temps de chargement de la page
    useEffect(() => {
        const handleLoad = () => {
            const loadTime = Date.now() - pageLoadTime;
            trackPageLoadTime('adhesion_page', loadTime);
        };

        if (document.readyState === 'complete') {
            handleLoad();
            return; // Pas besoin de cleanup si déjà chargé
        }

        window.addEventListener('load', handleLoad);
        return () => window.removeEventListener('load', handleLoad);
    }, [pageLoadTime, trackPageLoadTime]);

    // Gestion du code de parrainage
    useEffect(() => {
        const refCode = searchParams.get('ref');

        if (refCode) {
            setReferralCode(refCode);
            localStorage.setItem('udb_referral_code', refCode);
            console.log('📩 Code de parrainage détecté dans l\'URL:', refCode);

            // Tracking début adhésion avec parrainage
            trackAdhesionStart(refCode);
            trackAdhesionFunnelStart();
        } else {
            // Vérifier s'il y a un code stocké précédemment
            const storedCode = localStorage.getItem('udb_referral_code');
            if (storedCode) {
                setReferralCode(storedCode);
                console.log('💾 Code de parrainage récupéré du localStorage:', storedCode);

                // Tracking début adhésion avec parrainage stocké
                trackAdhesionStart(storedCode);
                trackAdhesionFunnelStart();
            } else {
                // Tracking début adhésion sans parrainage
                trackAdhesionStart();
                trackAdhesionFunnelStart();
            }
        }
    }, [searchParams, trackAdhesionStart, trackAdhesionFunnelStart]);

    const handleClose = () => {
        // Tracking abandon du processus via le service GTM
        try {
            if (typeof window !== 'undefined' && (window as any).dataLayer) {
                (window as any).dataLayer.push({
                    event: 'udb_adhesion_abandon',
                    abandon_reason: 'user_close',
                    has_referral: !!referralCode,
                    referral_code: referralCode || null,
                    timestamp: new Date().toISOString()
                });
            }
        } catch (error) {
            console.error('Erreur tracking abandon:', error);
            trackTechnicalError('tracking_abandon_error', (error as Error).message, 'adhesion_page');
        }

        localStorage.removeItem('udb_referral_code');
        navigate('/');
    };

    const handleSuccess = (memberData: MemberData) => {
        console.log('✅ Adhésion réussie:', memberData);

        try {
            // Tracking succès adhésion (événement UDB)
            trackAdhesionSuccess({
                member_number: memberData.member_number,
                first_name: memberData.first_name,
                last_name: memberData.last_name,
                city: memberData.city,
                province: memberData.province,
                country: memberData.country || 'Gabon',
                referral_code: memberData.referral_code
            });

            // Tracking conversion e-commerce (pour Analytics)
            trackAdhesionConversion({
                member_number: memberData.member_number,
                first_name: memberData.first_name,
                last_name: memberData.last_name,
                city: memberData.city,
                province: memberData.province,
                country: memberData.country || 'Gabon',
                referral_code: memberData.referral_code
            });

            // Tracking avancé avec données détaillées
            if (typeof window !== 'undefined' && (window as any).dataLayer) {
                (window as any).dataLayer.push({
                    event: 'udb_new_member_detailed',
                    user_id: memberData.member_number,
                    member_number: memberData.member_number,
                    member_name: `${memberData.first_name} ${memberData.last_name}`,
                    member_city: memberData.city,
                    member_province: memberData.province,
                    member_country: memberData.country || 'Gabon',
                    has_referral: !!memberData.referral_code,
                    referral_code: memberData.referral_code || null,
                    signup_timestamp: new Date().toISOString(),

                    // Données pour segmentation
                    user_properties: {
                        member_province: memberData.province,
                        member_city: memberData.city,
                        has_referral: !!memberData.referral_code,
                        signup_date: new Date().toISOString().split('T')[0]
                    },

                    // Données e-commerce pour Analytics
                    ecommerce: {
                        transaction_id: memberData.member_number,
                        value: 1,
                        currency: 'XAF',
                        items: [{
                            item_id: 'udb_membership',
                            item_name: 'Adhésion UDB',
                            category: 'Political Membership',
                            quantity: 1,
                            price: 1
                        }]
                    }
                });
            }

            // Tracking GTM natif (si configuré)
            if (typeof window !== 'undefined' && (window as any).gtag) {
                (window as any).gtag('event', 'sign_up', {
                    method: 'udb_adhesion',
                    member_number: memberData.member_number,
                    referral_code: memberData.referral_code,
                    custom_parameters: {
                        member_city: memberData.city,
                        member_province: memberData.province
                    }
                });

                // Configuration utilisateur pour tracking personnalisé
                (window as any).gtag('config', 'GA_MEASUREMENT_ID', {
                    user_id: memberData.member_number,
                    custom_map: {
                        'custom_dimension_1': 'member_province',
                        'custom_dimension_2': 'member_city'
                    }
                });
            }

        } catch (error) {
            console.error('Erreur tracking succès:', error);
            trackTechnicalError('tracking_success_error', (error as Error).message, 'adhesion_page');
        }

        // Nettoyer le code de parrainage après succès
        localStorage.removeItem('udb_referral_code');
    };

    return (
        <div className="min-h-screen">
            <br/><br/>

            {/* Bannière spéciale pour invitation */}
            {referralCode && (
                <div className="bg-gradient-to-r from-yellow-100 to-green-100 border-l-4 border-green-500 p-4 mb-6 mx-4 rounded-lg shadow-lg">
                    <div className="flex items-center">
                        <div className="text-2xl mr-3">🎉</div>
                        <div>
                            <h3 className="text-lg font-semibold text-green-800">
                                Invitation spéciale !
                            </h3>
                            <p className="text-green-700">
                                Vous avez été invité avec le code de parrainage <strong>{referralCode}</strong>.
                                Bénéficiez d'un accompagnement privilégié pour votre adhésion !
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Composant principal */}
            <AdhesionFlow
                onClose={handleClose}
                onSuccess={handleSuccess}
                referralCode={referralCode || undefined}
            />

            {/* Script de debug GTM (seulement en développement) */}
            {process.env.NODE_ENV === 'development' && (
                <div
                    dangerouslySetInnerHTML={{
                        __html: `
                            <script>
                                // Debug GTM Events
                                window.debugGTM = function() {
                                    console.log('📊 DataLayer Events:', window.dataLayer);
                                    return window.dataLayer ? window.dataLayer.filter(event => 
                                        event.event && event.event.startsWith('udb_')
                                    ) : [];
                                };
                                
                                // Test GTM
                                window.testGTM = function() {
                                    if (window.dataLayer) {
                                        window.dataLayer.push({
                                            event: 'udb_test_event',
                                            test_parameter: 'success',
                                            timestamp: new Date().toISOString()
                                        });
                                        console.log('✅ Test GTM event sent');
                                    } else {
                                        console.error('❌ GTM DataLayer not found');
                                    }
                                };
                                
                                console.log('🔧 GTM Debug disponible: window.debugGTM() et window.testGTM()');
                            </script>
                        `
                    }}
                />
            )}
        </div>
    );
};

export default AdhesionPage;
